import React, { useState, useEffect, useCallback } from 'react';
import { Gavel, User, Clock, Check, X, Loader2, TrendingUp, ChevronDown, ChevronUp, AlertCircle } from 'lucide-react';
import axios from '../lib/axios';
import { toast } from 'sonner';
import { API, useAuth, useCurrency } from '../App';
import PriceDisplay from './PriceDisplay';

const BidFeed = ({ listingId, sellerId, minimumBid, currency: listingCurrency = 'USD', onBidPlaced, onBidChange }) => {
  const { user, token } = useAuth();
  const { formatPrice, formatDualPrice, currency: userCurrency } = useCurrency();
  const [bids, setBids] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [placingBid, setPlacingBid] = useState(false);
  const [bidAmount, setBidAmount] = useState('');
  const [showAllBids, setShowAllBids] = useState(false);
  const [processingBidId, setProcessingBidId] = useState(null);
  
  const isOwner = user?.id === sellerId;
  
  // Get currency symbol based on user's preferred currency for the input field
  const currencySymbol = userCurrency === 'USD' ? '$' : 'XCG';
  
  // Convert minimum bid to user's preferred currency (number only, for placeholder)
  const USD_TO_XCG = 1.79;
  const XCG_TO_USD = 0.56;
  const convertedMinBid = minimumBid > 0 
    ? (listingCurrency === 'USD' && userCurrency === 'XCG' 
        ? minimumBid * USD_TO_XCG 
        : listingCurrency === 'XCG' && userCurrency === 'USD'
          ? minimumBid * XCG_TO_USD
          : minimumBid)
    : 0;
  
  // Format for placeholder (number only, no symbol)
  const formattedMinBid = convertedMinBid > 0 
    ? convertedMinBid.toLocaleString('nl-NL', { minimumFractionDigits: 0, maximumFractionDigits: 0 })
    : '';
  
  const fetchBids = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/listings/${listingId}/bids`);
      setBids(res.data.bids || []);
      setStats(res.data.stats || null);
    } catch (error) {
      console.error('Error fetching bids:', error);
    } finally {
      setLoading(false);
    }
  }, [listingId]);
  
  useEffect(() => {
    fetchBids();
    // Poll for new bids every 10 seconds
    const interval = setInterval(fetchBids, 10000);
    return () => clearInterval(interval);
  }, [fetchBids]);
  
  const handlePlaceBid = async (e) => {
    e.preventDefault();
    
    if (!user) {
      toast.error('Please login to place a bid');
      return;
    }
    
    const amount = parseFloat(bidAmount);
    if (!amount || amount <= 0) {
      toast.error('Please enter a valid bid amount');
      return;
    }
    
    // Convert user's input to listing currency for validation and submission
    let amountInListingCurrency = amount;
    if (userCurrency !== listingCurrency) {
      if (userCurrency === 'XCG' && listingCurrency === 'USD') {
        amountInListingCurrency = amount * XCG_TO_USD;
      } else if (userCurrency === 'USD' && listingCurrency === 'XCG') {
        amountInListingCurrency = amount * USD_TO_XCG;
      }
    }
    
    // Validate against minimum bid (both now in listing currency)
    if (minimumBid > 0 && amountInListingCurrency < minimumBid) {
      toast.error(`Bid must be at least ${formatPrice(minimumBid, listingCurrency)}`);
      return;
    }
    
    setPlacingBid(true);
    try {
      await axios.post(
        `${API}/listings/${listingId}/bids`,
        { amount: amountInListingCurrency });
      
      toast.success('Bid placed successfully!');
      setBidAmount('');
      fetchBids();
      if (onBidPlaced) onBidPlaced();
      if (onBidChange) onBidChange();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to place bid');
    } finally {
      setPlacingBid(false);
    }
  };
  
  const handleAcceptBid = async (bidId) => {
    if (!window.confirm('Accept this bid? This will mark your listing as reserved and notify all bidders.')) {
      return;
    }
    
    setProcessingBidId(bidId);
    try {
      await axios.post(
        `${API}/listings/${listingId}/bids/${bidId}/accept`);
      
      toast.success('Bid accepted! The buyer has been notified.');
      fetchBids();
      if (onBidChange) onBidChange();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to accept bid');
    } finally {
      setProcessingBidId(null);
    }
  };
  
  const handleRejectBid = async (bidId) => {
    setProcessingBidId(bidId);
    try {
      await axios.post(
        `${API}/listings/${listingId}/bids/${bidId}/reject`);
      
      toast.success('Bid rejected');
      fetchBids();
      if (onBidChange) onBidChange();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject bid');
    } finally {
      setProcessingBidId(null);
    }
  };
  
  const handleWithdrawBid = async (bidId) => {
    if (!window.confirm('Withdraw your bid?')) return;
    
    setProcessingBidId(bidId);
    try {
      await axios.delete(
        `${API}/listings/${listingId}/bids/${bidId}`);
      
      toast.success('Bid withdrawn');
      fetchBids();
      // Notify parent to refresh listing data
      if (onBidChange) onBidChange();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to withdraw bid');
    } finally {
      setProcessingBidId(null);
    }
  };
  
  const pendingBids = bids.filter(b => b.status === 'pending');
  const displayBids = showAllBids ? bids : bids.slice(0, 5);
  const highestBid = stats?.highest_bid || 0;
  
  if (loading) {
    return (
      <div className="bg-card rounded-2xl border border-border p-6">
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 text-primary animate-spin" />
        </div>
      </div>
    );
  }
  
  return (
    <div className="space-y-4" data-testid="bid-feed-section">
      {/* Bid Stats Header */}
      <div className="bg-gradient-to-r from-amber-500/10 to-orange-500/10 rounded-2xl border border-amber-500/20 p-5">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center">
              <Gavel className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="font-semibold text-foreground">Open for Bidding</h3>
              <p className="text-xs text-muted-foreground">
                {pendingBids.length} active bid{pendingBids.length !== 1 ? 's' : ''}
              </p>
            </div>
          </div>
          {highestBid > 0 && (
            <div className="text-right">
              <p className="text-xs text-muted-foreground">Highest Bid</p>
              <p className="text-lg font-bold text-amber-500">
                <PriceDisplay amount={highestBid} fromCurrency={listingCurrency} />
              </p>
            </div>
          )}
        </div>
        
        {minimumBid > 0 && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <TrendingUp className="w-4 h-4" />
            <span>Minimum bid: <PriceDisplay amount={minimumBid} fromCurrency={listingCurrency} size="sm" /></span>
          </div>
        )}
      </div>
      
      {/* Place Bid Form - For non-owners */}
      {!isOwner && user && (
        <div className="bg-card rounded-2xl border border-border p-5" data-testid="place-bid-form">
          <h4 className="font-medium text-foreground mb-3">Place Your Bid</h4>
          <form onSubmit={handlePlaceBid} className="space-y-3">
            <div className="flex gap-2">
              <div className="relative flex-1">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">
                  {currencySymbol}
                </span>
                <input
                  type="number"
                  value={bidAmount}
                  onChange={(e) => setBidAmount(e.target.value)}
                  placeholder={minimumBid > 0 ? `Min: ${formattedMinBid}` : 'Enter your bid'}
                  className={`w-full pr-3 py-2.5 rounded-xl border border-border bg-background text-foreground focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20 outline-none ${userCurrency === 'USD' ? 'pl-7' : 'pl-12'}`}
                  data-testid="bid-amount-input"
                />
              </div>
              <button
                type="submit"
                disabled={placingBid || !bidAmount}
                className="px-5 py-2.5 bg-gradient-to-r from-amber-500 to-orange-500 text-white font-medium rounded-xl hover:opacity-90 disabled:opacity-50 transition-all flex items-center gap-2"
                data-testid="submit-bid-btn"
              >
                {placingBid ? <Loader2 className="w-4 h-4 animate-spin" /> : <Gavel className="w-4 h-4" />}
                Bid
              </button>
            </div>
          </form>
        </div>
      )}
      
      {/* Login prompt for non-authenticated users */}
      {!user && (
        <div className="bg-card rounded-2xl border border-border p-5 text-center">
          <AlertCircle className="w-8 h-8 text-amber-500 mx-auto mb-2" />
          <p className="text-foreground font-medium">Login to place a bid</p>
          <p className="text-sm text-muted-foreground">Join the bidding on this item</p>
        </div>
      )}
      
      {/* Bids List */}
      {bids.length > 0 ? (
        <div className="bg-card rounded-2xl border border-border overflow-hidden" data-testid="bids-list">
          <div className="p-4 border-b border-border">
            <h4 className="font-medium text-foreground">All Bids</h4>
          </div>
          <div className="divide-y divide-border">
            {displayBids.map((bid, index) => {
              const isHighest = bid.amount === highestBid && bid.status === 'pending';
              const isMyBid = bid.bidder_id === user?.id;
              const isProcessing = processingBidId === bid.id;
              
              return (
                <div 
                  key={bid.id} 
                  className={`p-4 ${isHighest ? 'bg-amber-500/5' : ''} ${bid.status !== 'pending' ? 'opacity-60' : ''}`}
                  data-testid={`bid-item-${index}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium ${
                        isHighest ? 'bg-amber-500 text-white' : 'bg-muted text-muted-foreground'
                      }`}>
                        {bid.bidder_avatar ? (
                          <img src={bid.bidder_avatar} alt="" className="w-full h-full rounded-full object-cover" />
                        ) : (
                          bid.bidder_name?.[0]?.toUpperCase() || <User className="w-4 h-4" />
                        )}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-foreground text-sm">
                            {bid.bidder_name}
                            {isMyBid && <span className="text-xs text-primary ml-1">(You)</span>}
                          </span>
                          {isHighest && (
                            <span className="text-xs bg-amber-500 text-white px-2 py-0.5 rounded-full">
                              Highest
                            </span>
                          )}
                          {bid.status !== 'pending' && (
                            <span className={`text-xs px-2 py-0.5 rounded-full ${
                              bid.status === 'accepted' ? 'bg-green-500/20 text-green-500' :
                              bid.status === 'rejected' ? 'bg-red-500/20 text-red-500' :
                              'bg-muted text-muted-foreground'
                            }`}>
                              {bid.status}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          <Clock className="w-3 h-3" />
                          {new Date(bid.created_at).toLocaleString()}
                        </div>
                        {bid.message && (
                          <p className="text-xs text-muted-foreground mt-1 italic">"{bid.message}"</p>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-3">
                      <div className="text-right">
                        <PriceDisplay amount={bid.amount} fromCurrency={listingCurrency} size="md" className="font-bold" />
                      </div>
                      
                      {/* Owner actions */}
                      {isOwner && bid.status === 'pending' && (
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => handleAcceptBid(bid.id)}
                            disabled={isProcessing}
                            className="p-2 bg-green-500/10 text-green-500 rounded-lg hover:bg-green-500/20 transition-colors"
                            title="Accept bid"
                            data-testid={`accept-bid-${index}`}
                          >
                            {isProcessing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                          </button>
                          <button
                            onClick={() => handleRejectBid(bid.id)}
                            disabled={isProcessing}
                            className="p-2 bg-red-500/10 text-red-500 rounded-lg hover:bg-red-500/20 transition-colors"
                            title="Reject bid"
                            data-testid={`reject-bid-${index}`}
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      )}
                      
                      {/* Bidder can withdraw their own pending bid */}
                      {isMyBid && bid.status === 'pending' && !isOwner && (
                        <button
                          onClick={() => handleWithdrawBid(bid.id)}
                          disabled={isProcessing}
                          className="p-2 text-muted-foreground hover:text-red-500 rounded-lg transition-colors"
                          title="Withdraw bid"
                        >
                          {isProcessing ? <Loader2 className="w-4 h-4 animate-spin" /> : <X className="w-4 h-4" />}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          
          {/* Show more/less toggle */}
          {bids.length > 5 && (
            <button
              onClick={() => setShowAllBids(!showAllBids)}
              className="w-full p-3 text-sm text-primary hover:bg-primary/5 transition-colors flex items-center justify-center gap-1"
            >
              {showAllBids ? (
                <>Show Less <ChevronUp className="w-4 h-4" /></>
              ) : (
                <>Show All {bids.length} Bids <ChevronDown className="w-4 h-4" /></>
              )}
            </button>
          )}
        </div>
      ) : (
        <div className="bg-card rounded-2xl border border-border p-8 text-center">
          <Gavel className="w-12 h-12 text-muted-foreground mx-auto mb-3 opacity-50" />
          <p className="text-foreground font-medium">No bids yet</p>
          <p className="text-sm text-muted-foreground">Be the first to place a bid!</p>
        </div>
      )}
    </div>
  );
};

export default BidFeed;
